import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveUser } from '../../utils/auth';
import { supabase } from '../../utils/supabase';
import { createLogEntry } from '../../utils/storage';
import { setActiveEventId, DEFAULT_EVENT_ID } from '../../utils/event';

const RESERVED_MASTER_EVENT = 'ADMIN';

interface EventRow {
  id: string;
  event_code: string;
  event_name: string;
  status: string;
}

export default function Login() {
  const [eventId, setEventId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchWithRetry = async (fn: () => Promise<any>, retries = 3, delay = 1000): Promise<any> => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const result = await fn();
        if (result?.error && (result.error.message?.includes('Failed to fetch') || result.error.message?.includes('NetworkError'))) {
          if (attempt < retries) {
            await new Promise(res => setTimeout(res, delay * attempt));
            continue;
          }
        }
        return result;
      } catch (err: any) {
        if (attempt < retries && (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError'))) {
          await new Promise(res => setTimeout(res, delay * attempt));
          continue;
        }
        throw err;
      }
    }
    return { data: null, error: null };
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const trimmedEventId = eventId.trim().toUpperCase();
    const trimmedUsername = username.trim();
    const isMasterAttempt = trimmedEventId === RESERVED_MASTER_EVENT;

    try {
      // Resolve the event (case-insensitive). ADMIN is reserved for master admin — not a real event.
      let resolvedEventId: string | null = null;
      let eventStatus: string | null = null;

      if (!isMasterAttempt) {
        const result = await fetchWithRetry(() =>
          supabase
            .from('events')
            .select('id, event_code, event_name, status')
            .or(`id.ilike.${trimmedEventId},event_code.ilike.${trimmedEventId}`)
            .maybeSingle()
        );
        const { data: event, error: eventError } = result;

        if (eventError) {
          const isNetworkError = eventError.message?.includes('Failed to fetch') || eventError.message?.includes('NetworkError');
          if (isNetworkError) {
            setError('Unable to connect to the server. Please check your internet connection and try again.');
          } else {
            setError(`Connection error: ${eventError.message || 'Unable to reach server.'}`);
          }
          setLoading(false);
          return;
        }

        if (!event) {
          await createLogEntry(
            { id: 'unknown', username: trimmedUsername },
            'Failed Login',
            `Unknown Event ID: ${trimmedEventId}`
          );
          setError('Invalid Event ID.');
          setLoading(false);
          return;
        }

        resolvedEventId = (event as EventRow).id;
        eventStatus = (event as EventRow).status;
      }

      // Look up the user
      const userResult = await fetchWithRetry(() =>
        supabase
          .from('app_users')
          .select('id, username, password, display_name, role, is_active, is_online, created_at, created_by, last_login_at, logout_timer')
          .eq('username', trimmedUsername)
          .maybeSingle()
      );
      const { data: user, error: userError } = userResult;

      if (userError) {
        const isNetworkError = userError.message?.includes('Failed to fetch') || userError.message?.includes('NetworkError');
        if (isNetworkError) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else {
          setError(`Connection error: ${userError.message || 'Unable to reach server.'}`);
        }
        setLoading(false);
        return;
      }

      if (!user) {
        await createLogEntry(
          { id: 'unknown', username: trimmedUsername },
          'Failed Login',
          `User not found: ${trimmedUsername}`
        );
        setError('Invalid username or password.');
        setLoading(false);
        return;
      }

      // Master admin must log in through the reserved ADMIN event.
      if (isMasterAttempt && user.role !== 'master_admin') {
        setError('Invalid Event ID or credentials.');
        setLoading(false);
        return;
      }
      if (!isMasterAttempt && user.role === 'master_admin') {
        // Master admins use the ADMIN event id; block generic event login.
        setError('Master Admin must sign in using Event ID ADMIN.');
        setLoading(false);
        return;
      }

      if (user.password !== password) {
        await createLogEntry(
          { id: user.id, username: user.username },
          'Failed Login',
          'Invalid password attempt'
        );
        setError('Invalid username or password.');
        setLoading(false);
        return;
      }

      if (user.is_active === false) {
        setError('Your account has been deactivated. Please contact the administrator.');
        setLoading(false);
        return;
      }

      const dbEventId = isMasterAttempt ? null : resolvedEventId;

      // For normal events: verify the user is assigned to this event.
      if (!isMasterAttempt && dbEventId) {
        const { data: assignment } = await supabase
          .from('user_events')
          .select('event_id, role')
          .eq('user_id', user.id)
          .eq('event_id', dbEventId)
          .maybeSingle();

        if (!assignment) {
          await createLogEntry(
            { id: user.id, username: user.username },
            'Failed Login',
            `User not assigned to event ${trimmedEventId}`
          );
          setError('You are not assigned to this event.');
          setLoading(false);
          return;
        }

        // Verify event status permits login
        if (eventStatus === 'archived') {
          setError('This event has been archived. Contact the administrator.');
          setLoading(false);
          return;
        }
      }

      // Resolve full set of events this user can access
      let eventIds: string[] = [];
      if (!isMasterAttempt) {
        try {
          const { data: assignments } = await supabase
            .from('user_events')
            .select('event_id')
            .eq('user_id', user.id);
          eventIds = (assignments || []).map((a: { event_id: string }) => a.event_id);
        } catch (_e) { /* ignore */ }
      }

      // Update online status
      try {
        await supabase
          .from('app_users')
          .update({ is_online: true, last_login_at: new Date().toISOString() })
          .eq('id', user.id);
      } catch (_e) { /* ignore */ }

      // Set active event context (master admin defaults to legacy event; switchable in sidebar)
      setActiveEventId(isMasterAttempt ? DEFAULT_EVENT_ID : (dbEventId as string));

      // Save user to localStorage
      saveUser({
        id: user.id,
        username: user.username,
        displayName: user.display_name,
        role: user.role,
        createdAt: user.created_at,
        createdBy: user.created_by,
        isActive: user.is_active,
        lastLoginAt: new Date().toISOString(),
        isOnline: true,
        logoutTimer: user.logout_timer,
        eventIds,
      });

      // Log successful login
      await createLogEntry(
        { id: user.id, username: user.username },
        'Login',
        isMasterAttempt
          ? 'Master Admin logged in'
          : `User logged in to event ${trimmedEventId}`
      );

      navigate('/dashboard');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-white px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-teal-500 flex items-center justify-center">
              <i className="ri-hospital-line text-white text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">MedCamp Plus</h1>
            <p className="text-sm font-semibold text-gray-500 mt-1">Multi-Event Medical Camp Platform</p>
            <p className="text-sm text-gray-400 mt-2">Sign in with your Event ID to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event ID
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-calendar-event-line text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className={inputCls}
                  placeholder="e.g. MEDSPARKZ26"
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-user-line text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={inputCls}
                  placeholder="Enter username"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="Enter password"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Master Admin? Use Event ID <span className="font-semibold text-gray-600">ADMIN</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}